import React from "react";
import LegalLayout from "@/components/LegalLayout";
import ContactFormLink from "@/components/ContactFormLink";

export default function EthicalConsiderations() {
  return (
    <LegalLayout
      slug="ethics"
      eyebrow="Responsibility"
      title="Ethical Considerations"
      lastUpdated="May 26, 2026 · Reviewed by counsel"
    >
      <p>
        Armstrong K9 Registry exists to celebrate and
        preserve purpose-bred dogs through accurate record-keeping. We take
        an unapologetic stance against puppy mills and indiscriminate
        volume-breeding. Every member of the Registry is expected to uphold
        the standards below.
      </p>

      <h2>1. Responsible breeding first</h2>
      <ul>
        <li>Breed only dogs that are physically sound, temperamentally stable, and of appropriate age.</li>
        <li>Maintain a plan for every puppy produced — do not breed without confirmed, qualified homes or personal retention capacity.</li>
        <li>Space litters to prioritise the dam's health. Females should have adequate recovery between litters, and should not be bred on every heat cycle.</li>
        <li>Retire dams and sires at an age consistent with breed-specific veterinary guidance.</li>
      </ul>

      <h2>2. Health screening</h2>
      <ul>
        <li>Health test both sire and dam for the hereditary conditions known to occur in the breed (e.g. OFA / PennHIP for hips and elbows, relevant DNA panels, cardiac, ophthalmologic, thyroid where indicated).</li>
        <li>Record results honestly in the Registry's Health tab. Test results are permanent — editing or deleting them to obscure a poor result is a violation of these Terms.</li>
        <li>Disclose known hereditary risks to every prospective puppy buyer in writing.</li>
      </ul>

      <h2>3. Welfare standards</h2>
      <ul>
        <li>Provide adequate shelter, nutrition, exercise, socialisation, and veterinary care.</li>
        <li>Never tether or crate dogs for prolonged periods.</li>
        <li>Ensure puppies remain with their dam and littermates until at least 8 weeks (10+ weeks for small/toy breeds where appropriate).</li>
        <li>Provide puppies with age-appropriate early neurological stimulation, socialisation, and vet checks prior to placement.</li>
      </ul>

      <h2>4. Honest representation</h2>
      <ul>
        <li>Pedigree records you upload must be truthful. Do not register ancestors you cannot substantiate.</li>
        <li>Use our <em>Pedigree Additions</em> review queue for ancestors you don't have direct documentation for — they require admin review.</li>
        <li>Do not misrepresent breed, color, or titles.</li>
        <li>Use of the "AST" registration number in advertising must reflect an active, unrevoked record.</li>
      </ul>

      <h2>5. Ownership transfers</h2>
      <ul>
        <li>Transfers must only occur with the genuine consent of both seller and buyer.</li>
        <li>Include a written contract covering health guarantee, return-to-breeder clause, and spay/neuter expectations where applicable.</li>
        <li>Report suspected fraudulent transfers via our <a href="/contact">Contact Us</a> form.</li>
      </ul>

      <h2>6. Anti-cruelty commitment</h2>
      <p>
        The Registry reserves the right to suspend or permanently remove any
        member whose conduct is credibly reported as abusive, neglectful, or
        in violation of state or federal animal-welfare law. We cooperate
        with law enforcement when presented with a lawful request.
      </p>

      <h2>7. No tolerance for discrimination</h2>
      <p>
        Members must engage with other breeders, buyers, and administrators
        in a respectful, non-discriminatory manner. Harassment of any kind
        is grounds for immediate suspension.
      </p>

      <h2>8. The Dog's Voice — community standards</h2>
      <p>
        The Dog's Voice, our in-app social feed, is held to the same ethical bar as
        the rest of the Registry. We expect:
      </p>
      <ul>
        <li>
          <strong>Celebrate, don't tear down.</strong> The Dog's Voice is for sharing
          milestones, work, and life with our dogs — not for public attacks on
          other breeders, kennels, or owners.
        </li>
        <li>
          <strong>No politics or divisive off-topic content.</strong> Posts
          unrelated to dogs and breeding (elections, partisan issues, religion,
          social-media outrage) will be removed regardless of which side they
          take.
        </li>
        <li>
          <strong>No negativity campaigns.</strong> Coordinated negativity,
          pile-ons, or "subtweet" posts targeting other members are treated as
          harassment and enforced under Section 7 above.
        </li>
        <li>
          <strong>Truthfulness.</strong> Don't post unverified claims about
          other kennels' health testing, lineage, or business practices.
          Honest disputes belong in the Contact-Admin process, not on the feed.
        </li>
        <li>
          <strong>Respect privacy.</strong> Don't post other members' real
          names, addresses, phone numbers, or family details without consent.
          Don't share private DMs or transfer paperwork screenshots.
        </li>
        <li>
          <strong>Use the Report button.</strong> If you see a post that
          violates these standards, tap Report. Don't engage publicly — that
          tends to amplify the harm.
        </li>
      </ul>
      <p>
        Section 5 of the{" "}
        <a href="/terms">Terms of Service</a> contains the full,
        contractually-binding The Dog's Voice rules and the graduated enforcement
        ladder (removal → warning → temporary suspension → permanent ban).
      </p>

      <h2>9. Reporting concerns</h2>
      <p>
        If you witness conduct that violates these standards — from another
        Registry member or elsewhere — please report it through our{" "}
        <a href="/contact">Contact Us</a> form or email{" "}
        <a href="mailto:ethics@armstrongk9registry.com">ethics@armstrongk9registry.com</a>
        <ContactFormLink topic="ethics" subject="Ethics report" />.
        Reports are reviewed in confidence.
      </p>

      <p className="mt-10 text-sm text-[#6B6B6B] italic">
        These expectations are aspirational and operational. The Registry
        team reserves the right to update them as our community and the
        practice of responsible breeding evolve.
      </p>
    </LegalLayout>
  );
}
